'use client'

import type { FormEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'

import { Button, Icon } from '@graftcode/design-system'

import { ACADEMY_ROUTES } from '@/shared/academy.const'
import { ACADEMY_HEADER_NAV_TEST_IDS } from '@/shared/academy-test-ids.const'

import styles from './NavbarSearchBar.module.scss'

type NavbarSearchBarProps = {
  onOpen?: (opts?: { query?: string }) => void
}

export function NavbarSearchBar({ onOpen }: NavbarSearchBarProps) {
  const router = useRouter()
  const inputRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState('')
  const [shortcutLabel, setShortcutLabel] = useState('⌘K')

  useEffect(() => {
    setShortcutLabel(/Mac|iPhone|iPod|iPad/i.test(navigator.platform) ? '⌘K' : 'Ctrl+K')

    if (onOpen) {
      return undefined
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') {
        return
      }
      if (event.defaultPrevented) {
        return
      }
      const target = event.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return
      }
      event.preventDefault()
      inputRef.current?.focus()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onOpen])

  const navigateToSearch = (rawQuery: string) => {
    const q = rawQuery.trim()
    const base = `${ACADEMY_ROUTES.documentation}/search`
    const href = q ? `${base}?q=${encodeURIComponent(q)}` : base
    router.push(href)
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (onOpen) {
      onOpen({ query: value })
    } else {
      navigateToSearch(value)
    }
  }

  return (
    <form
      className={styles.searchBar}
      onSubmit={handleSubmit}
      role="search"
      aria-label="Documentation search"
      data-testid={ACADEMY_HEADER_NAV_TEST_IDS.searchBar}
    >
      <div className={styles.icon}>
        <Button type="submit" iconOnly size="l" variant="ghost" icon={Icon.SearchRegular} aria-label="Search" />
      </div>
      <input
        ref={inputRef}
        className={styles.input}
        type="search"
        name="q"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onFocus={() => {
          if (onOpen) {
            onOpen({ query: value })
          }
        }}
        placeholder="Search"
        enterKeyHint="search"
        aria-label="Search documentation"
      />
      <span className={styles.shortcut}>{shortcutLabel}</span>
    </form>
  )
}
